import { useEffect, useState } from 'react';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';
import { GroupBase } from 'react-select';
import { extractListItemsFromHTML } from '../../../utility/helpers';
import { useAppDispatch } from '../../../store/hooks';
import { getIndustries } from '../../../store/features/dashboard';
import { NUMBERS } from '../../../resources/number-constants';

type OptionType = {
  label: string;
  value: string;
};

type Additional = {
  page: number;
};

const ALL_OPTION: OptionType = { label: "All", value: "All" };

const IndustryEdit = ({ value, setModalData }: any) => {
  const dispatch = useAppDispatch();
  const [selectedIndustries, setIndustries] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [allWarning, setAllWarning] = useState<string | null>(null);

  const fetchIndustries = async (search: string) => {
    const res: any = await dispatch(getIndustries(search)).unwrap();
    return res?.data?.results || [];
  };

  const capitalizeFirstLetter = (val: string) => {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  const loadOptions: LoadOptions<OptionType, GroupBase<OptionType>, Additional> = async (
    inputValue,
    loadedOptions,
  ) => {
    if (!inputValue || inputValue.trim().length <= 2) {
      return {
        options: [ALL_OPTION],
        hasMore: false
      };
    }

    try {
      const industries: any = await fetchIndustries(inputValue);
      const options = industries.map((item: any) => ({
        label: capitalizeFirstLetter(item.name),
        value: capitalizeFirstLetter(item.name),
      }));
      return {
        options: [ALL_OPTION, ...options],
        hasMore: false,
      };
    } catch {
      return {
        options: [ALL_OPTION],
        hasMore: false
      };
    }
  };

  const handleSelect = (selected: OptionType | null) => {
    setAllWarning(null);
    if (!selected) return;

    if (selected.value === "All") {
      setIndustries([ALL_OPTION]);
      setInputValue('');
      return;
    }

    if (selectedIndustries.find(opt => opt.value === "All")) {
      setAllWarning("Please remove 'All' before adding other industries.");
      return;
    }

    if (!selectedIndustries.find(opt => opt.value === selected.value)) {
      setIndustries(prev => [...prev, selected]);
      setInputValue('');
    }
  };

  const handleRemove = (i: number) => {
    setIndustries(prev => prev.filter((_, index) => index !== i));
    setAllWarning(null);
  };

  useEffect(() => {
    const parsedValue = extractListItemsFromHTML(value);
    if (parsedValue?.length > 0) {
      if (parsedValue.some((item: string) => item === "All")) {
        setIndustries([ALL_OPTION]);
      } else {
        const parsedIndus = parsedValue.map((item: string) => ({
          label: item,
          value: item,
        }));
        setIndustries(parsedIndus);
      }
    }
  }, [value]);

  useEffect(() => {
    if (setModalData && typeof setModalData === 'function') {
      if (selectedIndustries.length === 0) {
        setModalData('');
      } else {
        const htmlList = `<ul>${selectedIndustries
          .map((item) => `<li>${item.label}</li>`)
          .join('')}</ul>`;
        setModalData(htmlList);
      }
    }
  }, [selectedIndustries, setModalData]);

  useEffect(() => {
    setIsDisabled(
      selectedIndustries.length === NUMBERS.TWO ||
      selectedIndustries.find(opt => opt.value === "All") !== undefined
    );
  }, [selectedIndustries]);
  
  return (
    <div>
      <AsyncPaginate<OptionType, GroupBase<OptionType>, Additional>
        value={null}
        loadOptions={loadOptions}
        onChange={handleSelect}
        inputValue={inputValue}
        onInputChange={(value, { action }) => {
          if (action === "input-change" && selectedIndustries.length < 2) {
            setInputValue(value);
          }
        }}
        onKeyDown={e => {
          if (e.key === " " && inputValue.trim().length === 0) {
            e.preventDefault();
          }
        }}
        isClearable
        debounceTimeout={300}
        placeholder="Type here to search industries..."
        classNamePrefix="async-select"
        isDisabled={isDisabled}
      />
      {isDisabled && selectedIndustries.find(opt => opt.value === "All") && (
        <div
          style={{
            fontSize: "14px",
            padding: "2px",
            color: "rgb(255, 96, 96)",
          }}
        >
          You have selected "All" industries. Remove it to choose specific industries.
        </div>
      )}

      {isDisabled && !selectedIndustries.find(opt => opt.value === "All") && (
        <div
          style={{
            fontSize: "14px",
            padding: "2px",
            color: "rgb(255, 96, 96)",
          }}
        >
          You may choose a maximum of two industries.
        </div>
      )}

      {allWarning && (
        <div
          style={{
            fontSize: "14px",
            padding: "2px",
            color: "#ff6060",
          }}
        >
          {allWarning}
        </div>
      )}

      {selectedIndustries.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h6>Selected:</h6>
          <ul style={{ padding: 0, listStyle: "none" }}>
            {selectedIndustries.map((item, i) => (
              <li key={i}>
                {i + 1}. <span>{item.label}</span>
                <span
                  onClick={() => handleRemove(i)}
                  style={{
                    color: "#ff6060",
                    textDecoration: "underline",
                    cursor: "pointer",
                    marginLeft: "12px",
                    fontSize: "14px",
                  }}
                >
                  Remove
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default IndustryEdit;
